// packages/web-ui/src/panels/chat-panel/ToolCallGroup.tsx
//
// Wrapper around a list of `<ToolCallBox>` entries on an assistant message.
// Renders the boxes directly when the message has 1–2 tool calls (no extra
// chrome) and folds 3+ calls into a single collapsible "Tool calls (N)"
// summary so long agentic turns don't overwhelm the chat scroll.
//
// Visual language matches the per-box card (cream surface, taupe border,
// Audi-red on error, status icons from lucide-react) so the group reads as
// a stack of the same primitive rather than a separate widget.

import { useEffect, useState, type JSX } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { ToolCallBox } from './ToolCallBox.js';
import type { ToolCallEntry } from '../../state/chat.js';

export interface ToolCallGroupProps {
  entries: ToolCallEntry[];
  onRetry?: (callId: string) => void;
  onRerun?: (callId: string) => void;
  openInSyside?: (path: string) => void;
}

/** Calls below this threshold render as individual cards (no group chrome). */
const GROUP_THRESHOLD = 3;

interface StatusMix {
  running: number;
  ok: number;
  error: number;
  cancelled: number;
}

function tallyStatus(entries: ToolCallEntry[]): StatusMix {
  const mix: StatusMix = { running: 0, ok: 0, error: 0, cancelled: 0 };
  for (const e of entries) {
    mix[e.status] += 1;
  }
  return mix;
}

function formatTotalDuration(entries: ToolCallEntry[], now: number): string {
  let total = 0;
  for (const e of entries) {
    const start = new Date(e.startedAt).getTime();
    const end = e.completedAt !== undefined ? new Date(e.completedAt).getTime() : now;
    total += Math.max(0, end - start);
  }
  const sec = total / 1000;
  if (sec < 60) return `${sec.toFixed(1)} s`;
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}m ${s}s`;
}

export function ToolCallGroup({
  entries,
  onRetry,
  onRerun,
  openInSyside,
}: ToolCallGroupProps): JSX.Element | null {
  const hasError = entries.some((e) => e.status === 'error');
  const hasRunning = entries.some((e) => e.status === 'running');
  // The group renders collapsed by default regardless of status. A contained
  // error still tints the border red and shows the "N error" badge in the
  // header, but the group no longer auto-expands on error (RB feedback — it
  // popped the whole stack open and created chat noise). The user expands the
  // group, then the failed box inside, when they want detail.
  const [expanded, setExpanded] = useState(false);
  const [tick, setTick] = useState(0);

  // Re-render every 100ms while any call is still running so the rolled-up
  // duration ticks alongside the individual cards inside.
  useEffect(() => {
    if (!hasRunning) return;
    const id = setInterval(() => setTick((t) => t + 1), 100);
    return () => clearInterval(id);
  }, [hasRunning]);

  if (entries.length === 0) return null;

  // 1–2 calls: stack of individual cards, no group chrome.
  if (entries.length < GROUP_THRESHOLD) {
    return (
      <>
        {entries.map((entry) => (
          <ToolCallBox
            key={entry.callId}
            entry={entry}
            onRetry={onRetry}
            onRerun={onRerun}
            openInSyside={openInSyside}
          />
        ))}
      </>
    );
  }

  const mix = tallyStatus(entries);
  // Tick reference keeps the memoiser invalidated while running.
  const duration = formatTotalDuration(entries, Date.now() - 0 * tick);
  const borderClass = hasError ? 'border-accent-red' : 'border-taupe-500';

  return (
    <div
      data-testid="tool-call-group"
      data-count={entries.length}
      data-has-error={hasError ? 'true' : 'false'}
      className={`bg-cream-50 my-2 overflow-hidden rounded-none border ${borderClass}`}
    >
      <button
        type="button"
        data-testid="tool-call-group-header"
        aria-expanded={expanded}
        aria-controls={`tool-group-body-${entries[0]?.callId ?? 'empty'}`}
        onClick={() => setExpanded((e) => !e)}
        className="flex h-7 w-full items-center gap-2 px-3 py-1 text-left"
      >
        <span className="text-xs font-semibold">Tool calls ({entries.length})</span>
        <span className="ml-auto flex items-center gap-2 text-xs text-taupe-700">
          {mix.ok > 0 ? (
            <span data-testid="tool-call-group-ok-count" className="text-accent-green">
              {mix.ok} OK
            </span>
          ) : null}
          {mix.running > 0 ? (
            <span data-testid="tool-call-group-running-count">{mix.running} running</span>
          ) : null}
          {mix.error > 0 ? (
            <span data-testid="tool-call-group-error-count" className="text-accent-red">
              {mix.error} error
            </span>
          ) : null}
          {mix.cancelled > 0 ? (
            <span data-testid="tool-call-group-cancelled-count">{mix.cancelled} cancelled</span>
          ) : null}
          <span data-testid="tool-call-group-duration">⏱ {duration}</span>
          {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
        </span>
      </button>

      {expanded ? (
        <div
          id={`tool-group-body-${entries[0]?.callId ?? 'empty'}`}
          data-testid="tool-call-group-body"
          className="border-t border-soft px-3 py-2"
        >
          {entries.map((entry) => (
            <ToolCallBox
              key={entry.callId}
              entry={entry}
              onRetry={onRetry}
              onRerun={onRerun}
              openInSyside={openInSyside}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}
